/**
 * Downscales an image file in the browser before upload so full-size photos
 * never leave the user's machine. Keeps every delivery channel happy: Twilio
 * rejects media over ~5MB (WhatsApp images) and the webchat widget loads
 * smaller images much faster on mobile connections.
 *
 * Fails open: any file it can't (or shouldn't) resize is returned unchanged,
 * so callers can pass every upload through it unconditionally.
 */

// Canvas re-encoding would strip GIF animation, so GIFs pass through as-is.
const RESIZABLE_TYPES = new Set([
	'image/jpeg',
	'image/png',
	'image/bmp',
	'image/webp',
]);

// Files at or under this size are already deliverable everywhere.
const SKIP_BYTES = 1_000_000;

// A downscaled PNG this small keeps its transparency; anything bigger falls
// back to JPEG so the result stays well under channel media caps (Twilio
// rejects WhatsApp images over 5MB).
const PNG_KEEP_BYTES = 2_000_000;

export interface DownscaleImageOptions {
	/** Longest edge of the output image, in pixels. */
	maxDimension?: number;
	/** JPEG encoding quality, 0-1. Ignored for PNG output. */
	quality?: number;
	/**
	 * Files at or under this size are returned unchanged. Lower the default
	 * for images that are served constantly (e.g. widget avatars), where even
	 * a few hundred KB is worth shrinking.
	 */
	skipBytes?: number;
}

const renameForType = (name: string, type: string): string => {
	if (type !== 'image/jpeg') return name;
	const baseName = name.replace(/\.[^.]+$/, '');
	return `${baseName || name}.jpg`;
};

export async function downscaleImage(
	file: File,
	options: DownscaleImageOptions = {}
): Promise<File> {
	const { maxDimension = 2000, quality = 0.85, skipBytes = SKIP_BYTES } = options;

	if (!RESIZABLE_TYPES.has(file.type) || file.size <= skipBytes) {
		return file;
	}

	try {
		const bitmap = await createImageBitmap(file);
		const scale = Math.min(
			1,
			maxDimension / Math.max(bitmap.width, bitmap.height)
		);

		const canvas = document.createElement('canvas');
		canvas.width = Math.round(bitmap.width * scale);
		canvas.height = Math.round(bitmap.height * scale);

		const context = canvas.getContext('2d');
		if (!context) return file;

		context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
		bitmap.close();

		const encode = (type: string) =>
			new Promise<Blob | null>((resolve) => {
				canvas.toBlob(resolve, type, quality);
			});

		const toFile = (blob: Blob, type: string) =>
			new File([blob], renameForType(file.name, type), { type });

		// PNG first, so transparency survives when the result is small enough.
		if (file.type === 'image/png') {
			const pngBlob = await encode('image/png');
			if (
				pngBlob &&
				pngBlob.size < file.size &&
				pngBlob.size <= PNG_KEEP_BYTES
			) {
				return toFile(pngBlob, 'image/png');
			}

			// PNG re-encoding didn't get small enough (typical for photos saved
			// as PNG) — composite onto white and fall through to JPEG, which
			// would otherwise render transparent regions black.
			context.globalCompositeOperation = 'destination-over';
			context.fillStyle = '#ffffff';
			context.fillRect(0, 0, canvas.width, canvas.height);
		}

		const jpegBlob = await encode('image/jpeg');

		// Re-encoding can lose (e.g. an already-optimized image); only swap in
		// the new bytes when they are actually smaller.
		if (!jpegBlob || jpegBlob.size >= file.size) return file;

		return toFile(jpegBlob, 'image/jpeg');
	} catch {
		return file;
	}
}
